import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import type { GeoJSONPolygon } from '../geo';
import { sb } from './client';
import type { RealtimeUnsubscribe, RoofOverride } from './types';

export async function getOverrides(): Promise<RoofOverride[]> {
  const c = sb();
  if (!c) return [];
  const { data, error } = await c.from('roof_overrides').select('*');
  if (error) throw error;
  return (data ?? []) as RoofOverride[];
}

/** Submit a drawn polygon. Contract: { site_id, search: { bbox: <GeoJSON Polygon> } }. */
export async function saveRoofOverride(
  siteId: string,
  polygon: GeoJSONPolygon,
): Promise<RoofOverride | null> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { data, error } = await c.rpc('save_roof_override', {
    p_site_id: siteId,
    search: { bbox: polygon },
  });
  if (error) throw error;
  return (data as RoofOverride) ?? null;
}

export async function deleteOverride(siteId: string): Promise<void> {
  const c = sb();
  if (!c) throw new Error('Not connected');
  const { error } = await c.from('roof_overrides').delete().eq('site_id', siteId);
  if (error) throw error;
}

/** Subscribe to roof_overrides changes. Returns an unsubscribe (source never did — fixed here). */
export function subscribeOverrides(onRow: (row: RoofOverride) => void): RealtimeUnsubscribe {
  const c = sb();
  if (!c) return () => {};
  const channel = c
    .channel('roof-overrides-changes')
    .on<RoofOverride>(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'roof_overrides' },
      (payload: RealtimePostgresChangesPayload<RoofOverride>) => {
        const row = payload.new as RoofOverride;
        if (row?.site_id) onRow(row);
      },
    )
    .subscribe();
  return () => {
    c.removeChannel(channel);
  };
}
